import { useMemo } from 'react';
import {
  type D2CPaymentTab,
  D2C_PAYMENT_TABS,
  D2C_PAYMENT_TAB_OPTIONS,
  getQuotePaymentOption,
} from '@shared/guidedPlanningPreferences';

import {
  type GuidedPlanningQuote,
  getPremiumAmount,
} from './use-quotes-display';

export interface PaymentTabData {
  tab: D2CPaymentTab;
  quotes: GuidedPlanningQuote[];
}

interface UseQuotesByPaymentTabOptions {
  quotes: GuidedPlanningQuote[] | undefined;
}

interface UseQuotesByPaymentTabResult {
  tabs: PaymentTabData[];
  populatedTabs: PaymentTabData[];
  emptyTabs: PaymentTabData[];
  defaultTab: D2CPaymentTab | undefined;
  totalCount: number;
}

/**
 * Groups quotes by payment frequency for the tab view.
 * Within each tab, quotes are sorted by:
 *   1. Premium amount ascending (cheapest first)
 *   2. ROI descending (best value first when premiums are equal)
 */
export function useQuotesByPaymentTab({
  quotes,
}: UseQuotesByPaymentTabOptions): UseQuotesByPaymentTabResult {
  return useMemo(() => {
    if (!quotes || quotes.length === 0) {
      const emptyTabs = D2C_PAYMENT_TABS.map(tab => ({ tab, quotes: [] }));
      return {
        tabs: emptyTabs,
        populatedTabs: [],
        emptyTabs,
        defaultTab: undefined,
        totalCount: 0,
      };
    }

    const tabQuotesMap = new Map<D2CPaymentTab, GuidedPlanningQuote[]>();
    for (const tab of D2C_PAYMENT_TABS) {
      tabQuotesMap.set(tab, []);
    }

    for (const quote of quotes) {
      const paymentOption = getQuotePaymentOption(quote);

      for (const tab of D2C_PAYMENT_TABS) {
        if (D2C_PAYMENT_TAB_OPTIONS[tab].includes(paymentOption)) {
          tabQuotesMap.get(tab)!.push(quote);
          break;
        }
      }
    }

    const tabs: PaymentTabData[] = D2C_PAYMENT_TABS.map(tab => ({
      tab,
      quotes: tabQuotesMap.get(tab)!.sort((a, b) => {
        const premiumDiff = getPremiumAmount(a) - getPremiumAmount(b);
        if (premiumDiff !== 0) return premiumDiff;
        return (b.roi ?? 0) - (a.roi ?? 0);
      }),
    }));

    const populatedTabs = tabs.filter(t => t.quotes.length > 0);
    const emptyTabs = tabs.filter(t => t.quotes.length === 0);
    const defaultTab = populatedTabs[0]?.tab;

    return {
      tabs,
      populatedTabs,
      emptyTabs,
      defaultTab,
      totalCount: quotes.length,
    };
  }, [quotes]);
}
